import axios from "axios";
import { API_BASE_URL } from "../constants";

/**
 * Resource shapes pulled from the generated OpenAPI types (see `make types`).
 * These annotate the API client so editors give autocomplete on payloads and
 * flag obvious shape mistakes. The .jsx app isn't type-checked in CI yet
 * (`npm run typecheck` only gates the generated enums) — this is the on-ramp.
 *
 * @typedef {import("../types/api").components["schemas"]["Memory"]} Memory
 * @typedef {import("../types/api").components["schemas"]["PatchedMemory"]} PatchedMemory
 * @typedef {import("../types/api").components["schemas"]["Task"]} Task
 * @typedef {import("../types/api").components["schemas"]["PatchedTask"]} PatchedTask
 * @typedef {import("../types/api").components["schemas"]["Project"]} Project
 * @typedef {import("../types/api").components["schemas"]["PatchedProject"]} PatchedProject
 * @typedef {import("../types/api").components["schemas"]["Snippet"]} Snippet
 * @typedef {import("../types/api").components["schemas"]["PatchedSnippet"]} PatchedSnippet
 * @typedef {import("../types/api").components["schemas"]["Document"]} Document
 * @typedef {import("../types/api").components["schemas"]["PatchedDocument"]} PatchedDocument
 * @typedef {import("../types/api").components["schemas"]["UserSlim"]} UserSlim
 * @typedef {import("../types/api").components["schemas"]["TaskActivity"]} TaskActivity
 * @typedef {import("../types/api").components["schemas"]["Attachment"]} Attachment
 * @typedef {import("../types/api").components["schemas"]["AttachmentUpload"]} AttachmentUpload
 * @typedef {import("../types/api").components["schemas"]["Drawing"]} Drawing
 * @typedef {import("../types/api").components["schemas"]["DrawingList"]} DrawingList
 * @typedef {import("../types/api").components["schemas"]["DrawingWrite"]} DrawingWrite
 * @typedef {import("../types/api").components["schemas"]["DrawingConflict"]} DrawingConflict
 */

// Create axios instance with base configuration
export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Store logout function reference (will be set by AuthContext)
let logoutFn = null;

export const setLogoutHandler = (logout) => {
  logoutFn = logout;
};

// Request interceptor - adds auth token to all requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("access_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor - handles 401 errors globally
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && logoutFn) {
      // Token expired or invalid - trigger logout
      logoutFn();
    }
    return Promise.reject(error);
  },
);

// Memory API methods
export const memoryApi = {
  getAll: (params, config = {}) => api.get("memory/", { params, ...config }),
  getReview: (deckId, config = {}) => {
    const params = deckId ? { deck: deckId } : undefined;
    return api.get("memory/review/", { params, ...config });
  },
  /** @param {Partial<Memory>} data */
  create: (data) => api.post("memory/", data),
  /** @param {PatchedMemory} data */
  update: (id, data) => api.patch(`memory/${id}/`, data),
  delete: (id) => api.delete(`memory/${id}/`),
  rate: (id, rating) => api.post(`memory/${id}/rate/`, { rating }),
};

// Decks API methods
export const decksApi = {
  /** One page; each deck carries `card_count` and `due_count`. */
  getAll: (params) => api.get("decks/", { params }),
  /** @param {{name: string, description?: string}} data */
  create: (data) => api.post("decks/", data),
  update: (id, data) => api.patch(`decks/${id}/`, data),
  /** Removes the deck only — its cards stay in your library. */
  delete: (id) => api.delete(`decks/${id}/`),
};

// Task API methods
export const taskApi = {
  getAll: (params) => api.get("tasks/", { params }),
  get: (id) => api.get(`tasks/${id}/`),
  /** @param {Partial<Task>} data */
  create: (data) => api.post("tasks/", data),
  /** @param {PatchedTask} data */
  update: (id, data) => api.patch(`tasks/${id}/`, data),
  delete: (id) => api.delete(`tasks/${id}/`),
  /** Take the task (atomic; 409 when someone already holds it). */
  claim: (id) => api.post(`tasks/${id}/claim/`),
  /** Let go of a claim without changing status. */
  release: (id) => api.post(`tasks/${id}/release/`),
  /** The thread, oldest first, paginated. `params.since` = ISO instant. */
  activity: (id, params) => api.get(`tasks/${id}/activity/`, { params }),
  /**
   * The board's fingerprint — `{stamp, count}` for what `getAll` with the
   * same `project` / `archived` params would list. Poll this; refetch the
   * list only when `stamp` changes (projects/task-live-refresh).
   * @param {{project?: number, archived?: string}} [params]
   */
  pulse: (params) => api.get("tasks/pulse/", { params }),
  /** Post a comment to the thread; resolves with the recorded event. */
  comment: (id, body) => api.post(`tasks/${id}/comments/`, { body }),
  /** Archive a finished task (400 if open, 409 if already archived). */
  archive: (id) => api.post(`tasks/${id}/archive/`),
  /** Restore an archived Done task (still Done, with a fresh 7 days). Canceled tasks are refused (400) — reopen them with a status change. */
  unarchive: (id) => api.post(`tasks/${id}/unarchive/`),
  /**
   * Archive every Done task you can see — or one project's, with
   * `{ project }`. Resolves with `{ archived: n }`.
   */
  archiveDone: ({ project } = {}) =>
    api.post("tasks/archive-done/", null, {
      params: project != null ? { project } : {},
    }),
};

// Task attachments (projects/file-attachments). Uploading is three steps
// so the bytes go straight to storage, never through the API: `start`
// returns a short-lived signed URL, `upload` PUTs the file there, and
// `complete` has the backend check it and publish it.
export const attachmentApi = {
  /** Ready files on the task, oldest first. Each download_url lasts ~10 min. */
  list: (taskId) => api.get(`tasks/${taskId}/attachments/`),
  /** @returns {Promise<{data: AttachmentUpload}>} */
  start: (taskId, { name, size, content_type }) =>
    api.post(`tasks/${taskId}/attachments/`, { name, size, content_type }),
  /**
   * PUT the file to the signed URL with EXACTLY the headers `start` returned
   * (they're part of the signature). Plain axios, not `api`: the signed URL
   * is its own credential, and the user's login token must never be sent to
   * the storage host.
   */
  upload: (url, file, headers, { onProgress, signal } = {}) =>
    axios.put(url, file, {
      headers,
      signal,
      onUploadProgress: (e) =>
        onProgress?.(e.total ? e.loaded / e.total : e.progress || 0),
    }),
  /** @returns {Promise<{data: Attachment}>} */
  complete: (id) => api.post(`attachments/${id}/complete/`),
  delete: (id) => api.delete(`attachments/${id}/`),
};

// Users: who am I, and who else is on this mono (humans and agents).
export const userApi = {
  me: () => api.get("users/me/"),
  /** Paginated directory of every active account — follow `next`. */
  household: (params) => api.get("users/household/", { params }),
  /** One person by exact username or email (any case) → slim user, or 404. */
  lookup: (q) => api.get("users/lookup/", { params: { q } }),
  /** Replace MY token (password re-confirmed); the old key dies at once. */
  rotateToken: (password) => api.post("users/me/rotate-token/", { password }),
  /** Set MY profile picture (PNG/JPEG/WebP/GIF ≤ 10 MB) → `{avatar_url}`. */
  setAvatar: (file) => api.put("users/me/avatar/", avatarForm(file), MULTIPART),
  removeAvatar: () => api.delete("users/me/avatar/"),
};

// The instance defaults to JSON, and axios would turn a FormData body into
// JSON under that header; multipart lets the browser add the boundary.
const MULTIPART = { headers: { "Content-Type": "multipart/form-data" } };

function avatarForm(file) {
  const form = new FormData();
  form.append("image", file);
  return form;
}

// Discussions (projects/discussions): household threads, panels whose
// answers stay hidden from the other panelists until they open, and "for
// you" (unanswered panels, unread mentions).
export const discussionApi = {
  /** One page, newest activity first; `params.for_me` = only what's for you. */
  list: (params) => api.get("discussions/", { params }),
  /** The thread as you see it (other panelists' answers hidden while closed). */
  get: (id) => api.get(`discussions/${id}/`),
  /** `{title, body, panelists?: [username]}` → the new discussion. */
  create: (data) => api.post("discussions/", data),
  /** Reply — or, as a panelist while the answers are closed, answer (409 if you have). */
  reply: (id, body) => api.post(`discussions/${id}/posts/`, { body }),
  /** You've read it, up to `readAt` (the newest post you were shown). */
  read: (id, readAt) =>
    api.post(`discussions/${id}/read/`, readAt ? { read_at: readAt } : {}),
  /** The asker side opens a panel's answers early. */
  openAnswers: (id) => api.post(`discussions/${id}/open-answers/`),
  /** People pin a decision; "" clears it. */
  decide: (id, decision) =>
    api.post(`discussions/${id}/decision/`, { decision }),
  delete: (id) => api.delete(`discussions/${id}/`),
  deletePost: (id, postId) => api.delete(`discussions/${id}/posts/${postId}/`),
  /** Link the task a post became. */
  linkTask: (id, postId, taskId) =>
    api.post(`discussions/${id}/posts/${postId}/task/`, { task_id: taskId }),
  /** A fingerprint of the thread, for live refresh. */
  pulse: (id) => api.get(`discussions/${id}/pulse/`),
  /** `{count}`: how many are for you — the nav's badge (D14). */
  forYou: () => api.get("discussions/for-you/"),
};

// The activity feed (projects/activity-feed): others' work on tasks you
// can see, one row per task per day in your time zone.
export const activityApi = {
  /**
   * One page of rows, newest first. `params`: `tz` (IANA), `page`,
   * `actor` (id), `kind` ("comments" | "finished"), `mine` (true = include
   * your own actions).
   */
  list: (params) => api.get("activity/", { params }),
  /** Mark the feed looked at → `{seen_at}`: the time it replaces (or null). */
  seen: () => api.post("activity/seen/"),
};

// Drawings (projects/drawings). A scene save sends the `version` it started
// from; a stale one is a 409 carrying DrawingConflict. Images live outside
// the scene: each is uploaded once under Excalidraw's own file id.
export const drawingApi = {
  /** One page of drawings you can see (no scenes); `params.search`. */
  getAll: (params) => api.get("drawings/", { params }),
  /** @returns {Promise<{data: Drawing}>} the scene plus a URL per image */
  get: (id) => api.get(`drawings/${id}/`),
  /** @param {DrawingWrite} data */
  create: (data) => api.post("drawings/", data),
  /** @param {DrawingWrite} data — `scene` needs `version`; 409 when stale */
  update: (id, data) => api.patch(`drawings/${id}/`, data),
  delete: (id) => api.delete(`drawings/${id}/`),
  /** Store one image (≤ 10 MB) → `{file_id, url}`; idempotent per id. */
  addFile: (id, fileId, blob) => {
    const form = new FormData();
    form.append("file_id", fileId);
    form.append("image", blob, fileId);
    return api.post(`drawings/${id}/files/`, form, MULTIPART);
  },
  /** The task's linked drawings you can see, plus `hidden` (others'). */
  taskLinks: (taskId) => api.get(`tasks/${taskId}/drawings/`),
  /** Link a drawing you can see (idempotent) → the link. */
  link: (taskId, drawingId) =>
    api.post(`tasks/${taskId}/drawings/`, { drawing: drawingId }),
  unlink: (taskId, drawingId) =>
    api.delete(`tasks/${taskId}/drawings/${drawingId}/`),
  /** Task managers: remove the links to drawings you can't see. */
  removeHidden: (taskId) => api.delete(`tasks/${taskId}/drawings/hidden/`),
  /** Replace the gallery preview (an exported PNG ≤ 2 MB). */
  setPreview: (id, blob) => {
    const form = new FormData();
    form.append("image", blob, "preview.png");
    return api.put(`drawings/${id}/preview/`, form, MULTIPART);
  },
};

// The House section (projects/yates-planner §5.5). A plan's edits go
// through `ops` as a batch with the `version` they were written against; a
// stale one is a 409 carrying PlanConflict (the current version and the
// latest change). Lengths in ops are strings with a unit ("3.810 m").
/**
 * @typedef {import("../types/api").components["schemas"]["HouseList"]} HouseList
 * @typedef {import("../types/api").components["schemas"]["HouseDetail"]} HouseDetail
 * @typedef {import("../types/api").components["schemas"]["PlanDetail"]} PlanDetail
 * @typedef {import("../types/api").components["schemas"]["PlanPulse"]} PlanPulse
 * @typedef {import("../types/api").components["schemas"]["PlanCompare"]} PlanCompare
 * @typedef {import("../types/api").components["schemas"]["OpsResponse"]} OpsResponse
 * @typedef {import("../types/api").components["schemas"]["StepResponse"]} StepResponse
 * @typedef {import("../types/api").components["schemas"]["PlanConflict"]} PlanConflict
 */
export const plannerApi = {
  /** @returns {Promise<{data: HouseList}>} houses you can see + may you import */
  houses: () => api.get("planner/houses/"),
  /** Staff, a person, in a household → 201 `{house, report}`; 409 `{detail, house}`. */
  importYates: () => api.post("planner/houses/import-yates/"),
  /** @returns {Promise<{data: HouseDetail}>} the house and its plans (no documents) */
  house: (id) => api.get(`planner/houses/${id}/`),
  /** `{layers: [{floor, data}]}` — the reviewed scan surfaces per floor. */
  scans: (id) => api.get(`planner/houses/${id}/scans/`),
  /** A new proposal copied from `from_plan` (default: the as-built) → 201 PlanDetail. */
  createPlan: (houseId, { name, from_plan } = {}) =>
    api.post(`planner/houses/${houseId}/plans/`, {
      name,
      ...(from_plan != null ? { from_plan } : {}),
    }),
  /** @returns {Promise<{data: PlanDetail}>} */
  plan: (id) => api.get(`planner/plans/${id}/`),
  /** @returns {Promise<{data: PlanPulse}>} what an open editor polls */
  pulse: (id) => api.get(`planner/plans/${id}/pulse/`),
  /** The §7 rows; `geometry` adds coordinates (metres, house frame). */
  summary: (id, { floor, geometry = false } = {}) =>
    api.get(`planner/plans/${id}/summary/`, {
      params: {
        ...(floor ? { floor } : {}),
        ...(geometry ? { geometry: "1" } : {}),
      },
    }),
  /** @returns {Promise<{data: PlanCompare}>} against its baseline, or `against` (a plan id) */
  compare: (id, { against } = {}) =>
    api.get(`planner/plans/${id}/compare/`, {
      params: against != null ? { against } : {},
    }),
  scanCheck: (id, { floor } = {}) =>
    api.get(`planner/plans/${id}/scan-check/`, {
      params: floor ? { floor } : {},
    }),
  /** Proposals only (403 on the as-built). */
  rename: (id, name) => api.patch(`planner/plans/${id}/`, { name }),
  /** Proposals; its creator, the house's owner or staff. */
  remove: (id) => api.delete(`planner/plans/${id}/`),
  /**
   * An edit batch. 200 OpsResponse; 400 `{detail, index}`; 409 PlanConflict.
   * @returns {Promise<{data: OpsResponse}>}
   */
  ops: (id, { version, ops, dry_run = false, via } = {}) =>
    api.post(`planner/plans/${id}/ops/`, {
      version,
      ops,
      dry_run,
      ...(via ? { via } : {}),
    }),
  /** @returns {Promise<{data: StepResponse}>} `revision` = the change you mean (409 if another is on top) */
  undo: (id, { version, revision, via } = {}) =>
    api.post(`planner/plans/${id}/undo/`, stepBody(version, revision, via)),
  /** @returns {Promise<{data: StepResponse}>} */
  redo: (id, { version, revision, via } = {}) =>
    api.post(`planner/plans/${id}/redo/`, stepBody(version, revision, via)),
  /** One page (20 rows, or `page_size` ≤ 100), newest first: `{count, next, previous, results}`. */
  revisions: (id, { page, page_size } = {}) =>
    api.get(`planner/plans/${id}/revisions/`, {
      params: {
        ...(page && page > 1 ? { page } : {}),
        ...(page_size ? { page_size } : {}),
      },
    }),
  /** `{materials: [...]}` — paints, floors, tiles, woods. */
  catalog: () => api.get("planner/catalog/"),
};

function stepBody(version, revision, via) {
  return {
    version,
    ...(revision != null ? { revision } : {}),
    ...(via ? { via } : {}),
  };
}

// Agents console (staff-only writes; every signed-in human may read).
// The token comes back ONLY from create and rotate — never on a GET.
export const agentApi = {
  list: (params) => api.get("agents/", { params }),
  get: (id) => api.get(`agents/${id}/`),
  /** @param {{username: string, description?: string}} data → 201 with `token` */
  create: (data) => api.post("agents/", data),
  update: (id, data) => api.patch(`agents/${id}/`, data),
  /** Staff or the agent's owner: set / remove its picture → `{avatar_url}`. */
  setAvatar: (id, file) =>
    api.put(`agents/${id}/avatar/`, avatarForm(file), MULTIPART),
  removeAvatar: (id) => api.delete(`agents/${id}/avatar/`),
  /** New token; the old one stops working immediately. */
  rotate: (id) => api.post(`agents/${id}/rotate/`),
  /**
   * Give the agent a password so it can sign in at /login and drive the
   * app as itself. 201 `{username, password}` — shown once. 409 if it
   * already has one.
   */
  provisionLogin: (id) => api.post(`agents/${id}/provision-login/`),
  /** Replace that password; every live session of the agent stops working. */
  resetLogin: (id) => api.post(`agents/${id}/reset-login/`),
  deactivate: (id) => api.post(`agents/${id}/deactivate/`),
  reactivate: (id) => api.post(`agents/${id}/reactivate/`),
  /** The agent's thread events on tasks you can see, newest first. */
  activity: (id, params) => api.get(`agents/${id}/activity/`, { params }),
};

// Your household (household sharing Q4) — people only. Every call answers
// with the whole page state except invite (the new invite) and
// removeInvite (204).
export const householdApi = {
  get: () => api.get("household/"),
  /** Start one with you as its first member → 201 state. */
  start: (name) => api.post("household/", { name }),
  rename: (name) => api.patch("household/", { name }),
  leave: () => api.post("household/leave/"),
  /** Invite by exact username or email → 201 invite; 400 `{who}` otherwise. */
  invite: (who) => api.post("household/invites/", { who }),
  /** Withdraw one your household sent, or decline one sent to you. */
  removeInvite: (id) => api.delete(`household/invites/${id}/`),
  accept: (id) => api.post(`household/invites/${id}/accept/`),
};

// Strength training (projects/strength-training §4). Personal: every call
// is the signed-in person's own (an agent's lands under its human).
// Every single-session response (open, get, start, patch, finish) is a
// TrainingSession carrying `pain_reports` — the pain reported in it,
// oldest first; the list's rows don't.
// Exercises are addressed by `key`, not id. Two answers are not errors
// and come back as values, so no caller hand-parses a status:
//   - sessions.open(): 204 (nothing open) → `{data: null, status: 204}`
//     (axios would otherwise hand back `data: ""`);
//   - sessions.start(): 409 (one is already open) → `{conflict: {session,
//     detail}, status: 409}` — open that session instead (it may have been
//     started by an agent, or in another tab).
// Every other non-2xx rejects as usual: read it with parseApiError /
// apiErrorMessage (services/apiError.js), which flattens the nested
// serializers' errors to dotted paths ("bands.0.assist", "sets.1.reps").
/**
 * @typedef {import("../types/api").components["schemas"]["TrainingExercise"]} TrainingExercise
 * @typedef {import("../types/api").components["schemas"]["TrainingExerciseWrite"]} TrainingExerciseWrite
 * @typedef {import("../types/api").components["schemas"]["TrainingSettings"]} TrainingSettings
 * @typedef {import("../types/api").components["schemas"]["TrainingNextRequest"]} TrainingNextRequest
 * @typedef {import("../types/api").components["schemas"]["TrainingNext"]} TrainingNext
 * @typedef {import("../types/api").components["schemas"]["TrainingPrescribeRequest"]} TrainingPrescribeRequest
 * @typedef {import("../types/api").components["schemas"]["TrainingPrescribe"]} TrainingPrescribe
 * @typedef {import("../types/api").components["schemas"]["TrainingSummary"]} TrainingSummary
 * @typedef {import("../types/api").components["schemas"]["TrainingSessionRow"]} TrainingSessionRow
 * @typedef {import("../types/api").components["schemas"]["TrainingSessionDetail"]} TrainingSession
 * @typedef {import("../types/api").components["schemas"]["TrainingSessionPain"]} TrainingSessionPain
 * @typedef {import("../types/api").components["schemas"]["TrainingSessionStart"]} TrainingSessionStart
 * @typedef {import("../types/api").components["schemas"]["TrainingSessionConflict"]} TrainingSessionConflict
 * @typedef {import("../types/api").components["schemas"]["TrainingFinish"]} TrainingFinish
 * @typedef {import("../types/api").components["schemas"]["TrainingPlan"]} TrainingPlan
 * @typedef {import("../types/api").components["schemas"]["TrainingCheckin"]} TrainingCheckin
 * @typedef {import("../types/api").components["schemas"]["TrainingSet"]} TrainingSet
 * @typedef {import("../types/api").components["schemas"]["TrainingSetWrite"]} TrainingSetWrite
 * @typedef {import("../types/api").components["schemas"]["TrainingSetBatchResult"]} TrainingSetBatchResult
 * @typedef {import("../types/api").components["schemas"]["TrainingPainReportWrite"]} TrainingPainReportWrite
 * @typedef {import("../types/api").components["schemas"]["TrainingPainReportRead"]} TrainingPainReportRead
 * @typedef {{count: number, next: string|null, previous: string|null, results: TrainingSessionRow[]}} TrainingSessionPage
 * @typedef {{session: number, detail: string}} TrainingStartConflict
 */

/** Only the params that were given (an empty one is left out of the URL). */
const given = (params) =>
  Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) => value !== undefined && value !== null && value !== "",
    ),
  );

export const trainingApi = {
  exercises: {
    /**
     * Built-ins plus yours, unpaginated (a bounded catalog). Filters:
     * `muscle`, `equipment`, `q` (name or key contains), `archived: true`.
     * @returns {Promise<{data: TrainingExercise[]}>}
     */
    list: ({ muscle, equipment, q, archived } = {}, config = {}) =>
      api.get("training/exercises/", {
        params: given({ muscle, equipment, q, archived: archived || null }),
        ...config,
      }),
    /** @returns {Promise<{data: TrainingExercise}>} 404 for an unknown key */
    get: (key) => api.get(`training/exercises/${key}/`),
    /** @param {TrainingExerciseWrite} data → 201 TrainingExercise (key c-…) */
    create: (data) => api.post("training/exercises/", data),
    /** @param {Partial<TrainingExerciseWrite>} data → 200; a built-in is 403 */
    update: (key, data) => api.patch(`training/exercises/${key}/`, data),
    /** 204 removed, or 200 with the row when sets use it (archived instead). */
    remove: (key) => api.delete(`training/exercises/${key}/`),
  },
  settings: {
    /** @returns {Promise<{data: TrainingSettings}>} defaults with `saved: false` until the first PATCH */
    get: (config = {}) => api.get("training/settings/", config),
    /**
     * Only the top-level fields you send change — but `equipment` and
     * `bands` are each replaced whole (send the whole block), and
     * `seeded_loads` merges per key (null removes one).
     * @param {Partial<TrainingSettings>} data
     * @returns {Promise<{data: TrainingSettings}>}
     */
    patch: (data) => api.patch("training/settings/", data),
  },
  /**
   * The next session's plan with a reason for every number. A read (it
   * writes nothing) — a POST only because the check-in is a body. Pass
   * `config.signal` to abort a superseded request.
   * @param {TrainingNextRequest} body
   * @returns {Promise<{data: TrainingNext}>}
   */
  next: (body = {}, config = {}) => api.post("training/next/", body, config),
  /**
   * Plan items for exercises the person chose (1–10 keys, each once), each
   * prescribed exactly as next/ would today — the same check-in object,
   * the same item shape. A read, like next/. `notices` say when one can't
   * be done today (a painful joint, missing equipment); it's prescribed
   * anyway. An unknown key (or an archived one) is a 400 at its index:
   * `{exercises: {"1": ["No exercise with that key — …"]}}`.
   * @param {TrainingPrescribeRequest} body `{exercises: [key, …], checkin?}`
   * @returns {Promise<{data: TrainingPrescribe}>} `{units, items, notices}`
   */
  prescribe: (body, config = {}) =>
    api.post("training/prescribe/", body, config),
  /** @returns {Promise<{data: TrainingSummary}>} per muscle and per main lift */
  summary: (config = {}) => api.get("training/summary/", config),
  sessions: {
    /**
     * One page (20 rows, or `page_size` ≤ 100), newest first. `from` / `to`
     * are YYYY-MM-DD days in the settings' time zone.
     * @returns {Promise<{data: TrainingSessionPage}>}
     */
    list: ({ page, page_size, from, to } = {}, config = {}) =>
      api.get("training/sessions/", {
        params: given({
          page: page && page > 1 ? page : null,
          page_size,
          from,
          to,
        }),
        ...config,
      }),
    /**
     * The open session with its sets, or none. A session idle for over 6
     * hours reads as closed (D18).
     * @returns {Promise<{data: TrainingSession|null, status: number}>} `data: null` on 204
     */
    open: async (config = {}) => {
      const response = await api.get("training/sessions/open/", config);
      if (response.status === 204) return { data: null, status: 204 };
      return response;
    },
    /** @returns {Promise<{data: TrainingSession}>} 404 once discarded */
    get: (id) => api.get(`training/sessions/${id}/`),
    /**
     * Start one — from a plan (re-validated by the server), a template, or
     * blank. 201 → the session (its `notices` may say a stale one was
     * closed on the way, D18). 409 (one is already open) resolves too, as
     * `{conflict: {session, detail}, status: 409}`: check `conflict` first
     * and open that session. Any other failure rejects.
     * @param {TrainingSessionStart} body
     * @returns {Promise<{data: TrainingSession, status: number, conflict?: undefined} | {conflict: TrainingStartConflict, status: 409, data?: undefined}>}
     */
    start: async (body = {}) => {
      try {
        return await api.post("training/sessions/", body);
      } catch (err) {
        const data = err?.response?.data;
        if (err?.response?.status === 409 && data && data.session != null) {
          return {
            conflict: { session: data.session, detail: data.detail || "" },
            status: 409,
          };
        }
        throw err;
      }
    },
    /**
     * Edit the plan, template, minutes or check-in. `plan` and `checkin`
     * are replaced whole; there's no version check (last writer wins).
     * @param {{plan?: TrainingPlan|null, checkin?: TrainingCheckin|null, template?: string, minutes?: number|null}} body
     * @returns {Promise<{data: TrainingSession}>}
     */
    patch: (id, body) => api.patch(`training/sessions/${id}/`, body),
    /**
     * Close it with optional feedback `{muscle | "all": {pump, workload}}`;
     * finishing again updates the feedback.
     * @param {TrainingFinish} body
     * @returns {Promise<{data: TrainingSession}>}
     */
    finish: (id, body = {}) => api.post(`training/sessions/${id}/finish/`, body),
    /** Hard delete with its sets → 204; 403 when it isn't yours to discard (D19). */
    discard: (id) => api.delete(`training/sessions/${id}/`),
  },
  sets: {
    /**
     * Log sets, all or nothing (≤ 50). Each carries its own `client_id`:
     * resend the SAME ids on a retry and nothing is logged twice. 201 if
     * any set is new, 200 when every one was already logged; a discarded
     * session is a 404.
     * @param {TrainingSetWrite[]} sets
     * @returns {Promise<{data: TrainingSetBatchResult}>}
     */
    log: (sessionId, sets) =>
      api.post(`training/sessions/${sessionId}/sets/`, { sets }),
    /**
     * Fix a set — any of reps, load, rir, band, reps_left/right, performed_at.
     * @param {Partial<TrainingSetWrite>} body
     * @returns {Promise<{data: TrainingSet}>}
     */
    patch: (sessionId, setId, body) =>
      api.patch(`training/sessions/${sessionId}/sets/${setId}/`, body),
    /** → 204; 403 when it isn't yours to delete (`can_delete`, D19). */
    remove: (sessionId, setId) =>
      api.delete(`training/sessions/${sessionId}/sets/${setId}/`),
  },
  /**
   * Report pain in a joint: exercises loading it are avoided for a week.
   * @param {TrainingPainReportWrite} body
   * @returns {Promise<{data: TrainingPainReportRead}>} 201
   */
  pain: (body) => api.post("training/pain/", body),
};

// Investing (projects/investing spec §4, §5): the research desk — the
// companies we research, the theses they're scored against, verdicts,
// research notes — the person's settings, and the fund side (the target
// and its versions, the executor's snapshots, sync plans and fills, the
// scoreboard and its records). Everything is the signed-in person's own
// (an agent's lands under its person). Companies are addressed by symbol,
// theses by slug, notes, versions, plans and fills by number; each is
// checked against the API's own pattern before it reaches a URL (D10), and
// a bad one is a rejected promise like any other failed call (nothing is
// sent). Agent-written text in these responses (summaries, evidence,
// thesis text, reasons, a target's title and notes, review notes) is
// untrusted — markdown through the tightened MarkdownBlock, short text
// without its bidi controls (D8). Money and weights are decimal strings:
// format them as strings, never through float arithmetic. The
// human-gated writes — retracting a note, retiring or reviving a thesis,
// the settings, deciding a target change, acknowledging a fill — answer
// 403 to anything but a person in their own browser; read the reason with
// parseApiError / apiErrorMessage.
/**
 * @typedef {import("../types/api").components["schemas"]["InvestingCompanyRow"]} InvestingCompanyRow
 * @typedef {import("../types/api").components["schemas"]["PaginatedInvestingCompanyRowList"]} InvestingCompanyPage
 * @typedef {import("../types/api").components["schemas"]["InvestingCompany"]} InvestingCompany
 * @typedef {import("../types/api").components["schemas"]["PatchedInvestingCompanyPatch"]} InvestingCompanyPatch
 * @typedef {import("../types/api").components["schemas"]["PatchedInvestingVerdict"]} InvestingVerdict
 * @typedef {import("../types/api").components["schemas"]["InvestingThesisRow"]} InvestingThesisRow
 * @typedef {import("../types/api").components["schemas"]["PaginatedInvestingThesisRowList"]} InvestingThesisPage
 * @typedef {import("../types/api").components["schemas"]["InvestingThesis"]} InvestingThesis
 * @typedef {import("../types/api").components["schemas"]["PatchedInvestingThesisPatch"]} InvestingThesisPatch
 * @typedef {import("../types/api").components["schemas"]["InvestingSettings"]} InvestingSettings
 * @typedef {import("../types/api").components["schemas"]["PatchedInvestingSettingsPatch"]} InvestingSettingsPatch
 * @typedef {{verdict?: string, thesis?: string, sector?: string, q?: string, researched?: "yes"|"no", in_universe?: "yes"|"no", proposed?: "yes"|"no", ordering?: string, page?: number}} InvestingCompanyQuery
 * @typedef {import("../types/api").components["schemas"]["InvestingTarget"]} InvestingTarget
 * @typedef {import("../types/api").components["schemas"]["InvestingTargetVersion"]} InvestingTargetVersion
 * @typedef {import("../types/api").components["schemas"]["PaginatedInvestingTargetVersionRowList"]} InvestingTargetVersionPage
 * @typedef {import("../types/api").components["schemas"]["InvestingSnapshot"]} InvestingSnapshot
 * @typedef {import("../types/api").components["schemas"]["PaginatedInvestingSnapshotRowList"]} InvestingSnapshotPage
 * @typedef {import("../types/api").components["schemas"]["InvestingSyncPlan"]} InvestingSyncPlan
 * @typedef {import("../types/api").components["schemas"]["PaginatedInvestingSyncPlanListRowList"]} InvestingSyncPlanPage
 * @typedef {import("../types/api").components["schemas"]["InvestingFill"]} InvestingFill
 * @typedef {import("../types/api").components["schemas"]["PaginatedInvestingFillList"]} InvestingFillPage
 * @typedef {import("../types/api").components["schemas"]["InvestingScoreboard"]} InvestingScoreboard
 * @typedef {import("../types/api").components["schemas"]["PaginatedInvestingContributionList"]} InvestingContributionPage
 * @typedef {import("../types/api").components["schemas"]["PaginatedInvestingDistributionList"]} InvestingDistributionPage
 */

// The API's own patterns (backend/investing/constants.py SYMBOL_RE, SLUG_RE).
export const INVESTING_SYMBOL_RE = /^[A-Z][A-Z0-9.-]{0,9}$/;
export const INVESTING_SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const INVESTING_SLUG_MAX = 60;
const ID_MAX = 2 ** 31 - 1;

const isSymbol = (value) =>
  typeof value === "string" && INVESTING_SYMBOL_RE.test(value);
const isSlug = (value) =>
  typeof value === "string" &&
  value.length <= INVESTING_SLUG_MAX &&
  INVESTING_SLUG_RE.test(value);
const isId = (value) => Number.isInteger(value) && value > 0 && value <= ID_MAX;

/** Run `call` only when `ok`; otherwise a rejected promise saying why. */
const checked = (ok, message, call) =>
  ok ? call() : Promise.reject(new Error(message));
const BAD_SYMBOL = "Not a symbol: use an upper-case ticker like NVDA or BRK.B.";
const BAD_SLUG = "Not a thesis slug: use lowercase words joined by hyphens.";
const BAD_NOTE = "Not a research note id.";
const BAD_VERSION = "Not a target version number.";
const BAD_PLAN = "Not a sync plan id.";
const BAD_FILL = "Not a fill id.";

export const investingApi = {
  companies: {
    /**
     * One page of the research list (100 rows, the API's page size). Only
     * the filters given are sent, and page 1 is no page. `ordering`:
     * symbol (default), market_cap, last_researched_at or score:<slug>;
     * a leading - reverses it (nulls last). A page past the end is a 404.
     * @param {InvestingCompanyQuery} [query]
     * @returns {Promise<{data: InvestingCompanyPage}>}
     */
    list: (
      { verdict, thesis, sector, q, researched, in_universe, proposed, ordering, page } = {},
      config = {},
    ) =>
      api.get("investing/companies/", {
        params: given({
          verdict,
          thesis,
          sector,
          q,
          researched,
          in_universe,
          proposed,
          ordering,
          page: page && page > 1 ? page : null,
        }),
        ...config,
      }),
    /**
     * One company's page: the newest 20 notes (`notes_total` beside them;
     * retracted ones only with `retracted`) and decisions
     * (`verdict_history_total`), the pending `proposal`, the screen.
     * @returns {Promise<{data: InvestingCompany}>} 404 for no such company
     */
    get: (symbol, { retracted = false } = {}, config = {}) =>
      checked(isSymbol(symbol), BAD_SYMBOL, () =>
        api.get(`investing/companies/${symbol}/`, {
          params: retracted ? { retracted: "yes" } : {},
          ...config,
        }),
      ),
    /**
     * Link its research task or discussion (one you can see; null unlinks),
     * or de-list it. Send only what changes.
     * @param {InvestingCompanyPatch} data
     * @returns {Promise<{data: InvestingCompany}>} the company's page
     */
    patch: (symbol, data) =>
      checked(isSymbol(symbol), BAD_SYMBOL, () =>
        api.patch(`investing/companies/${symbol}/`, data),
      ),
    /**
     * The whole verdict with a reason (always): what's left out is cleared.
     * 403 for an agent on a verdict a person set (D6).
     * @param {InvestingVerdict} data
     * @returns {Promise<{data: InvestingCompany}>}
     */
    verdict: (symbol, data) =>
      checked(isSymbol(symbol), BAD_SYMBOL, () =>
        api.patch(`investing/companies/${symbol}/verdict/`, data),
      ),
  },
  notes: {
    /**
     * Retract a note, with a reason — a person in their own browser only.
     * @returns {Promise<{data: InvestingCompany}>} the company's page
     */
    retract: (id, reason) =>
      checked(isId(id), BAD_NOTE, () =>
        api.post(`investing/notes/${id}/retract/`, { reason }),
      ),
  },
  theses: {
    /**
     * One page (100), by slug. `status`: active or retired.
     * @returns {Promise<{data: InvestingThesisPage}>}
     */
    list: ({ status, page } = {}, config = {}) =>
      api.get("investing/theses/", {
        params: given({ status, page: page && page > 1 ? page : null }),
        ...config,
      }),
    /**
     * One thesis's page: the argument, and the newest 20 revisions with
     * `revisions_total` beside them. The companies it scores aren't on it:
     * `companies.list({thesis: slug, ordering: "-score:" + slug})` pages
     * them, highest score first.
     * @returns {Promise<{data: InvestingThesis}>} 404 for no such thesis
     */
    get: (slug, config = {}) =>
      checked(isSlug(slug), BAD_SLUG, () =>
        api.get(`investing/theses/${slug}/`, config),
      ),
    /**
     * Change it with a reason (kept as a revision). Changing `status`
     * (retire / revive) is a person's, in their own browser.
     * @param {InvestingThesisPatch} data
     * @returns {Promise<{data: InvestingThesis}>}
     */
    patch: (slug, data) =>
      checked(isSlug(slug), BAD_SLUG, () =>
        api.patch(`investing/theses/${slug}/`, data),
      ),
  },
  settings: {
    /** @returns {Promise<{data: InvestingSettings}>} with `can_edit` and the newest 50 changes */
    get: (config = {}) => api.get("investing/settings/", config),
    /**
     * Any of the settings with a reason (required) — a person in their own
     * browser only. Caps merge per key; locked fields are a 400.
     * @param {InvestingSettingsPatch} data
     * @returns {Promise<{data: InvestingSettings}>}
     */
    patch: (data) => api.patch("investing/settings/", data),
  },
  // --- The fund side (PR 4's API; PR 5's pages) -----------------------------
  target: {
    /**
     * The current target (the approved version, null before the first) and
     * the change open against it (the proposed version, else the newest
     * draft) with its diff, and how many versions are open.
     * @returns {Promise<{data: InvestingTarget}>}
     */
    get: (config = {}) => api.get("investing/target/", config),
    versions: {
      /**
       * The version history, newest first, 50 a page. `status`: draft,
       * proposed, approved, declined or superseded. A page past the end is
       * a 404.
       * @param {{status?: string, page?: number}} [query]
       * @returns {Promise<{data: InvestingTargetVersionPage}>}
       */
      list: ({ status, page } = {}, config = {}) =>
        api.get("investing/target/versions/", {
          params: given({ status, page: page && page > 1 ? page : null }),
          ...config,
        }),
      /**
       * One version in full: holdings, cap breaches and overrides, its diff.
       * @returns {Promise<{data: InvestingTargetVersion}>} 404 for no such version
       */
      get: (number, config = {}) =>
        checked(isId(number), BAD_VERSION, () =>
          api.get(`investing/target/versions/${number}/`, config),
        ),
      /**
       * Approve or decline a proposed version as a whole, bound to the
       * `revision` the person saw — a person in their own browser only
       * (403 for anything else). 409 when it isn't proposed any more, it
       * changed since (a stale revision), the target it was submitted
       * against moved, or approving has no executor that can act. Approved,
       * the response's `sync_task` is the board task it created. The reason
       * is optional and sent only when given.
       * @param {{revision: number, decision: "approved"|"declined", reason?: string}} body
       * @returns {Promise<{data: InvestingTargetVersion}>}
       */
      decide: (number, { revision, decision, reason }) =>
        checked(isId(number), BAD_VERSION, () =>
          api.post(`investing/target/versions/${number}/decide/`, {
            revision,
            decision,
            ...(reason ? { reason } : {}),
          }),
        ),
    },
  },
  snapshots: {
    /**
     * The executor's readings of the account, newest first, 100 a page —
     * figures only (positions are on `latest`).
     * @returns {Promise<{data: InvestingSnapshotPage}>}
     */
    list: ({ page } = {}, config = {}) =>
      api.get("investing/snapshots/", {
        params: given({ page: page && page > 1 ? page : null }),
        ...config,
      }),
    /**
     * The newest reading in full: positions, realized P&L, broker orders,
     * Robinhood's approval reading.
     * @returns {Promise<{data: InvestingSnapshot}>} 404 before the first
     */
    latest: (config = {}) => api.get("investing/snapshots/latest/", config),
  },
  plans: {
    /**
     * Sync plans, newest first, 50 a page, without their orders.
     * @returns {Promise<{data: InvestingSyncPlanPage}>}
     */
    list: ({ page } = {}, config = {}) =>
      api.get("investing/plans/", {
        params: given({ page: page && page > 1 ? page : null }),
        ...config,
      }),
    /**
     * One plan with its orders (size, limit, ref_id, status, review notes,
     * `after_sells`) and what it skipped.
     * @returns {Promise<{data: InvestingSyncPlan}>} 404 for no such plan
     */
    get: (id, config = {}) =>
      checked(isId(id), BAD_PLAN, () => api.get(`investing/plans/${id}/`, config)),
  },
  fills: {
    /**
     * Fills, newest first, 100 a page. `unacknowledged: "yes"` is the red
     * banner's source (its `count` is the banner's number);
     * `unauthorized`: yes/no; `plan`: the fills linked to that plan's orders.
     * @param {{unacknowledged?: "yes"|"no", unauthorized?: "yes"|"no", plan?: number, page?: number}} [query]
     * @returns {Promise<{data: InvestingFillPage}>}
     */
    list: ({ unacknowledged, unauthorized, plan, page } = {}, config = {}) =>
      api.get("investing/fills/", {
        params: given({
          unacknowledged,
          unauthorized,
          plan,
          page: page && page > 1 ? page : null,
        }),
        ...config,
      }),
    /**
     * Say you've seen an unauthorized fill, optionally with what it was —
     * a person in their own browser only. The reason is sent only when
     * given.
     * @returns {Promise<{data: InvestingFill}>} 409 for a linked fill
     */
    acknowledge: (id, reason) =>
      checked(isId(id), BAD_FILL, () =>
        api.post(`investing/fills/${id}/acknowledge/`, reason ? { reason } : {}),
      ),
  },
  scoreboard: {
    /**
     * The account against the same money held in the benchmark, at the
     * latest snapshot, with `method` saying how each figure is computed.
     * @returns {Promise<{data: InvestingScoreboard}>} 404 before the first snapshot
     */
    get: (config = {}) => api.get("investing/scoreboard/", config),
  },
  contributions: {
    /**
     * Deposits and withdrawals, 100 a page, each with the benchmark's
     * close on its date.
     * @returns {Promise<{data: InvestingContributionPage}>}
     */
    list: ({ page } = {}, config = {}) =>
      api.get("investing/contributions/", {
        params: given({ page: page && page > 1 ? page : null }),
        ...config,
      }),
  },
  distributions: {
    /**
     * The benchmark's distributions (by ex-dividend date), 100 a page.
     * @returns {Promise<{data: InvestingDistributionPage}>}
     */
    list: ({ page } = {}, config = {}) =>
      api.get("investing/benchmark-distributions/", {
        params: given({ page: page && page > 1 ? page : null }),
        ...config,
      }),
  },
};

// Project API methods
export const projectApi = {
  getAll: (params) => api.get("projects/", { params }),
  /** @param {Partial<Project>} data */
  create: (data) => api.post("projects/", data),
  /** @param {PatchedProject} data */
  update: (id, data) => api.patch(`projects/${id}/`, data),
  delete: (id) => api.delete(`projects/${id}/`),
};

// Snippet API methods. Lookup is by slug, not numeric id.
export const snippetApi = {
  getAll: (params) => api.get("snippets/", { params }),
  /** Allowed `language` values for code snippets — single source of truth. */
  languages: () => api.get("snippets/languages/"),
  get: (slug) => api.get(`snippets/${slug}/`),
  /** @param {Partial<Snippet>} data */
  create: (data) => api.post("snippets/", data),
  /** @param {PatchedSnippet} data */
  update: (slug, data) => api.patch(`snippets/${slug}/`, data),
  delete: (slug) => api.delete(`snippets/${slug}/`),
  /** Your own private copy of a snippet you can see → 201 the new snippet. */
  copy: (slug) => api.post(`snippets/${slug}/copy/`),
};

// Domain search storefront. Gated behind auth during dev (backend
// DOMAINS_PUBLIC_ACCESS), so these use the standard authed `api` client.
export const domainsApi = {
  /** Availability + price for a list of full domains (e.g. ["foo.com","foo.io"]). */
  check: (names, config = {}) =>
    api.get("domains/check", { params: { names: names.join(",") }, ...config }),
  /** Brandable SLD name ideas for a seed word. */
  suggest: (seed, count) =>
    api.get("domains/suggest", { params: { seed, count } }),
};

// Knowledge documents (the ingestion engine / second brain). Notes are
// created and edited as markdown (`markdown_source`); web pages are ingested
// by `url`. `content` is server-sanitized canonical HTML, read-only.
export const documentApi = {
  getAll: (params) => api.get("knowledge/documents/", { params }),
  get: (id) => api.get(`knowledge/documents/${id}/`),
  /** @param {Partial<Document>} data — exactly one of markdown_source | url */
  create: (data) => api.post("knowledge/documents/", data),
  /** @param {PatchedDocument} data */
  update: (id, data) => api.patch(`knowledge/documents/${id}/`, data),
  delete: (id) => api.delete(`knowledge/documents/${id}/`),
  /** Retry LLM enrichment for a `failed`/`pending` document. */
  enrich: (id) => api.post(`knowledge/documents/${id}/enrich/`),
  /**
   * Your own private copy of a document you can see (one shared with you
   * is read + copy) → 201 the copy, or 200 the one you already have.
   */
  copy: (id) => api.post(`knowledge/documents/${id}/copy/`),
  /** Semantically nearest other documents (empty until embeddings exist). */
  related: (id) => api.get(`knowledge/documents/${id}/related/`),
  /**
   * Full-text search; websearch syntax (quotes, OR, -exclusions). `tags`
   * (slugs) narrows to documents carrying ALL of them; the response's
   * `unknown_tags` names any slug that isn't in the library.
   */
  search: (q, { tags = [] } = {}, config = {}) =>
    api.get("knowledge/search/", {
      params: tags.length ? { q, tags: tags.join(",") } : { q },
      ...config,
    }),
};

// Knowledge tags: read the vocabulary / approval queue, gate transitions.
// Tags are only ever created by enrichment — there is no create here.
export const documentTagApi = {
  getAll: (params) => api.get("knowledge/tags/", { params }),
  approve: (id) => api.post(`knowledge/tags/${id}/approve/`),
  reject: (id) => api.post(`knowledge/tags/${id}/reject/`),
  merge: (id, intoId) =>
    api.post(`knowledge/tags/${id}/merge/`, { into: intoId }),
};

// Home videos (projects/home-videos): one family video at a time —
// upload, let Gemini draft the scenes, then name, fix and confirm them.
// Household-visible (D11). The upload's chunks do NOT go through here:
// they're PUT with plain fetch to the resumable session URL (a write
// credential for GCS that must never carry our bearer token — see
// components/homeVideos/lib/chunkUpload.js). The analysis endpoints
// (estimate/, analysis/…) follow the slice-2 contract in
// projects/home-videos/todo.md; only a person may start or retry them.
/**
 * @typedef {import("../types/api").components["schemas"]["Video"]} HomeVideo
 * @typedef {import("../types/api").components["schemas"]["Scene"]} HomeVideoScene
 * @typedef {import("../types/api").components["schemas"]["VideoPerson"]} HomeVideoPerson
 * @typedef {import("../types/api").components["schemas"]["Person"]} HomeVideoHouseholdPerson
 * @typedef {import("../types/api").components["schemas"]["SearchHit"]} HomeVideoSearchHit
 */

/** `?person=1&person=2` — DRF's getlist, not axios' default `person[]=`. */
function homeVideoSearchParams({ q = "", people = [], labels = [], page = 1 }) {
  const params = new URLSearchParams();
  if (q) params.set("q", q);
  people.forEach((id) => params.append("person", String(id)));
  labels.forEach((id) => params.append("label", String(id)));
  if (page > 1) params.set("page", String(page));
  return params;
}

const HV = "home-videos/";

export const homeVideosApi = {
  /** The library, newest first (12 a page). */
  list: (params, config = {}) => api.get(HV, { params, ...config }),
  /** `{title, file_name, size, content_type, duration_s, recorded_on,
   * recorded_on_approximate}` → 201 `{video, session_url, chunk_bytes, size}`.
   * Keep `session_url` in memory only: it's a week-long write credential. */
  create: (data) => api.post(HV, data),
  get: (id, config = {}) => api.get(`${HV}${id}/`, config),
  /** `{title?, recorded_on?, recorded_on_approximate?}`. */
  update: (id, data) => api.patch(`${HV}${id}/`, data),
  /** The uploader only (`can_manage`): the file, poster, scenes and its people go. */
  remove: (id) => api.delete(`${HV}${id}/`),
  /** For Resume: `{session_url, committed_bytes, size, chunk_bytes}` (409 once finished or expired). */
  uploadSession: (id) => api.get(`${HV}${id}/upload-session/`),
  /** Check what arrived → the ready video (400 marks it failed with the reason; 409 not all there). */
  complete: (id) => api.post(`${HV}${id}/complete/`),
  /** `{url, content_type, expires_at}` — a 6 h signed URL for `<video src>`. */
  playback: (id) => api.get(`${HV}${id}/playback/`),
  /** The browser-captured poster frame (an image Blob). */
  setPoster: (id, blob) => {
    const form = new FormData();
    form.append("image", blob, "poster.webp");
    return api.put(`${HV}${id}/poster/`, form, MULTIPART);
  },

  // Analysis (slice 2's contract): a person presses Analyse; the page
  // drives one bounded step per `next` call until `step === "done"`.
  estimate: (id, config = {}) => api.get(`${HV}${id}/estimate/`, config),
  analysis: (id, config = {}) => api.get(`${HV}${id}/analysis/`, config),
  analyseNext: (id) => api.post(`${HV}${id}/analysis/next/`),
  retryPart: (id, index) =>
    api.post(`${HV}${id}/analysis/parts/${index}/retry/`),

  scenes: {
    /** By start time, 100 a page — follow `next`. */
    list: (videoId, params, config = {}) =>
      api.get(`${HV}${videoId}/scenes/`, { params, ...config }),
    /** `{start_s, end_s, title?, summary?}` → 201 the scene (a person's is confirmed). */
    create: (videoId, data) => api.post(`${HV}${videoId}/scenes/`, data),
    update: (videoId, id, data) =>
      api.patch(`${HV}${videoId}/scenes/${id}/`, data),
    remove: (videoId, id) => api.delete(`${HV}${videoId}/scenes/${id}/`),
    /** → `{first, second}`. */
    split: (videoId, id, atS) =>
      api.post(`${HV}${videoId}/scenes/${id}/split/`, { at_s: atS }),
    mergeNext: (videoId, id) =>
      api.post(`${HV}${videoId}/scenes/${id}/merge-next/`),
    confirm: (videoId, id) => api.post(`${HV}${videoId}/scenes/${id}/confirm/`),
    /** → `{confirmed}`: how many scenes were still drafts. */
    confirmAll: (videoId) => api.post(`${HV}${videoId}/scenes/confirm-all/`),
    addPerson: (videoId, id, videoPersonId) =>
      api.post(`${HV}${videoId}/scenes/${id}/appearances/`, {
        video_person: videoPersonId,
      }),
    removePerson: (videoId, id, videoPersonId) =>
      api.delete(`${HV}${videoId}/scenes/${id}/appearances/${videoPersonId}/`),
    /** `{label}` (an id) or `{kind, name}` (made if new) → the scene. */
    addLabel: (videoId, id, data) =>
      api.post(`${HV}${videoId}/scenes/${id}/labels/`, data),
    removeLabel: (videoId, id, labelId) =>
      api.delete(`${HV}${videoId}/scenes/${id}/labels/${labelId}/`),
    /** `{text, at_s, speaker?}` → 201 the quote. */
    addQuote: (videoId, id, data) =>
      api.post(`${HV}${videoId}/scenes/${id}/quotes/`, data),
    removeQuote: (videoId, id, quoteId) =>
      api.delete(`${HV}${videoId}/scenes/${id}/quotes/${quoteId}/`),
  },

  videoPeople: {
    /** Everyone told apart in the video, 100 a page — follow `next`. */
    list: (videoId, params, config = {}) =>
      api.get(`${HV}${videoId}/people/`, { params, ...config }),
    /** *This is…*: `{person?, name?, description?}` → 201. */
    create: (videoId, data) => api.post(`${HV}${videoId}/people/`, data),
    update: (videoId, id, data) =>
      api.patch(`${HV}${videoId}/people/${id}/`, data),
    /** `{person}` or `{name}`: named in every scene at once. */
    name: (videoId, id, data) =>
      api.post(`${HV}${videoId}/people/${id}/name/`, data),
    /** `other` was this person too; they go. */
    merge: (videoId, id, otherId) =>
      api.post(`${HV}${videoId}/people/${id}/merge/`, { other: otherId }),
    dismiss: (videoId, id) =>
      api.post(`${HV}${videoId}/people/${id}/dismiss/`),
    acceptSuggestion: (videoId, id) =>
      api.post(`${HV}${videoId}/people/${id}/accept-suggestion/`),
  },

  people: {
    /** The household's people by name (12 a page); `{q?, video?, page?}`. */
    list: (params, config = {}) =>
      api.get(`${HV}people/`, { params, ...config }),
    create: (data) => api.post(`${HV}people/`, data),
    update: (id, data) => api.patch(`${HV}people/${id}/`, data),
    remove: (id) => api.delete(`${HV}people/${id}/`),
    photos: (id, params, config = {}) =>
      api.get(`${HV}people/${id}/photos/`, { params, ...config }),
    /** A reference photo: PNG/JPEG/WebP ≤ 10 MB, `year` optional, `fromVideo` for a frame. */
    addPhoto: (id, blob, { year = null, fromVideo = null, name = "photo" } = {}) => {
      const form = new FormData();
      form.append("image", blob, name);
      if (year !== null && year !== undefined && year !== "")
        form.append("year", String(year));
      if (fromVideo) form.append("from_video", String(fromVideo));
      return api.post(`${HV}people/${id}/photos/`, form, MULTIPART);
    },
    removePhoto: (id, photoId) =>
      api.delete(`${HV}people/${id}/photos/${photoId}/`),
    /** The reel builder: every scene they're in, totals, limits, suggestions. */
    reelCandidates: (id, config = {}) =>
      api.get(`${HV}people/${id}/reel-candidates/`, config),
  },

  /** Reels (projects/home-video-reels): one MP4 of a person's scenes. */
  reels: {
    /** The household's reels, newest first (12 a page). */
    list: (params, config = {}) => api.get(`${HV}reels/`, { params, ...config }),
    get: (id, config = {}) => api.get(`${HV}reels/${id}/`, config),
    /** `{person, scenes: [id]}` → the reel, queued (a person). */
    create: (data) => api.post(`${HV}reels/`, data),
    /** The same body → `{clips, seconds, refused}`, making nothing. */
    preview: (data, config = {}) => api.post(`${HV}reels/preview/`, data, config),
    cancel: (id) => api.post(`${HV}reels/${id}/cancel/`),
    remake: (id) => api.post(`${HV}reels/${id}/remake/`),
    remove: (id) => api.delete(`${HV}reels/${id}/`),
    /** `{url, filename}`: a 15-minute link to the MP4. */
    download: (id) => api.get(`${HV}reels/${id}/download/`),
  },

  /** Places, activities, events for autocomplete: `{kind?, q?, video?}`. */
  labels: (params, config = {}) =>
    api.get(`${HV}labels/`, { params, ...config }),

  /** Scenes across every video: `{q, people: [id], labels: [id], page}`. */
  search: (query, config = {}) =>
    api.get(`${HV}search/`, {
      params: homeVideoSearchParams(query),
      ...config,
    }),
};

/**
 * Live trivia (projects/trivia). The host's calls go through the signed-in
 * client; a player's go through `playClient`, which carries no mono
 * credentials and no 401 → log-out handler: players have no account, and
 * name themselves with the X-Trivia-Player header (D5).
 *
 * @typedef {import("../types/api").components["schemas"]["HostGame"]} HostGame
 * @typedef {import("../types/api").components["schemas"]["PlayerState"]} PlayerState
 * @typedef {import("../types/api").components["schemas"]["JoinResponse"]} JoinResponse
 */
export const triviaApi = {
  /** @returns {Promise<{data: HostGame}>} */
  create: (secondsPerQuestion) =>
    api.post("trivia/games/", { seconds_per_question: secondsPerQuestion }),
  /** @returns {Promise<{data: HostGame}>} */
  get: (id, config = {}) => api.get(`trivia/games/${id}/`, config),
  start: (id) => api.post(`trivia/games/${id}/start/`),
  reveal: (id) => api.post(`trivia/games/${id}/reveal/`),
  next: (id, expectedIndex) =>
    api.post(`trivia/games/${id}/next/`, { expected_index: expectedIndex }),
};

const playClient = axios.create({ baseURL: API_BASE_URL });
const asPlayer = (token, config = {}) => ({
  ...config,
  headers: { ...config.headers, "X-Trivia-Player": token },
});

export const playApi = {
  /** @returns {Promise<{data: JoinResponse}>} */
  join: (code, nickname) => playClient.post("trivia/play/join/", { code, nickname }),
  /** @returns {Promise<{data: PlayerState}>} */
  state: (token, config) => playClient.get("trivia/play/state/", asPlayer(token, config)),
  /** @returns {Promise<{data: PlayerState}>} */
  answer: (token, index, choice) =>
    playClient.post("trivia/play/answer/", { index, choice }, asPlayer(token)),
};

// Auth API methods (these don't need the auth interceptor for login)
export const authApi = {
  login: (username, password) =>
    axios.post(`${API_BASE_URL}token/`, { username, password }),
  googleLogin: (accessToken) =>
    axios.post(`${API_BASE_URL}auth/google/`, { access_token: accessToken }),
  // Raw axios on purpose: this endpoint authenticates with the submitted
  // credentials, and a wrong password must not trip the global 401
  // interceptor (which would log the user out of the app).
  getAuthToken: (username, password) =>
    axios.post(`${API_BASE_URL}token/auth/`, { username, password }),
};

export default api;
